/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  faArrowUpRightFromSquare,
  faCheck,
  faDownload,
  faUserPlus,
  faXmark,
} from "@/utils/iconLibraryTreeShake";

export const Route = createFileRoute("/dev/banners-toasts")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: BannersToastsPreview,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-slate-200 mb-3 border-b border-slate-700 pb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

function BannerShell({
  icon,
  iconBg,
  title,
  children,
}: {
  icon: ReactNode;
  iconBg: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="w-80 bg-slate-900 rounded-md px-4 py-3 shadow-lg border border-slate-700">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
          >
            {icon}
          </div>
          <p className="text-base font-semibold text-white">{title}</p>
        </div>
        <button
          type="button"
          className="text-slate-400 hover:text-slate-200 p-1 -m-1"
          aria-label="Dismiss"
        >
          <FontAwesomeIcon icon={faXmark} className="text-sm" />
        </button>
      </div>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

const noop = () => {};

function BannersToastsPreview() {
  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-2">
        Banners & Toasts Preview
      </h1>
      <p className="text-slate-400 mb-8 text-sm">
        Dev-only page showing all banner and toast variants.
      </p>

      {/* PWA Install Banner variants */}
      <Section title="PWA Install Banner">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Chromium */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Chromium (Install prompt)</p>
            <BannerShell
              icon={
                <FontAwesomeIcon icon={faDownload} className="text-white text-sm" />
              }
              iconBg="bg-blue-600"
              title="Install Galaxie"
            >
              <p className="text-sm text-slate-300">
                Get instant access from your home screen with offline support.
              </p>
              <div className="flex items-center justify-between mt-3 gap-x-2">
                <button
                  type="button"
                  onClick={noop}
                  className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={noop}
                  className="flex-1 rounded bg-blue-600 hover:bg-blue-500 px-3 py-1.5 text-sm font-medium text-white"
                >
                  Install
                </button>
              </div>
            </BannerShell>
          </div>

          {/* iOS */}
          <div>
            <p className="text-xs text-slate-500 mb-2">iOS (Safari instructions)</p>
            <BannerShell
              icon={
                <FontAwesomeIcon icon={faDownload} className="text-white text-sm" />
              }
              iconBg="bg-blue-600"
              title="Install Galaxie"
            >
              <p className="text-sm text-slate-300">
                Tap{" "}
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="text-blue-400 mx-0.5"
                />{" "}
                Share, then{" "}
                <span className="font-medium text-white">"Add to Home Screen"</span>
              </p>
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={noop}
                  className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
                >
                  Got it
                </button>
              </div>
            </BannerShell>
          </div>

          {/* macOS Safari */}
          <div>
            <p className="text-xs text-slate-500 mb-2">macOS Safari</p>
            <BannerShell
              icon={
                <FontAwesomeIcon icon={faDownload} className="text-white text-sm" />
              }
              iconBg="bg-blue-600"
              title="Install Galaxie"
            >
              <p className="text-sm text-slate-300">
                In the menu bar, click{" "}
                <span className="font-medium text-white">File</span> →{" "}
                <span className="font-medium text-white">"Add to Dock"</span>
              </p>
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={noop}
                  className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
                >
                  Got it
                </button>
              </div>
            </BannerShell>
          </div>
        </div>
      </Section>

      {/* Sign Up Banner */}
      <Section title="Sign Up Banner">
        <BannerShell
          icon={<FontAwesomeIcon icon={faUserPlus} className="text-white text-sm" />}
          iconBg="bg-violet-600"
          title="Create a Free Account"
        >
          <ul className="mt-0 space-y-1.5">
            <li className="flex items-start gap-2 text-sm text-slate-300">
              <FontAwesomeIcon
                icon={faCheck}
                className="mt-0.5 text-violet-400 text-xs shrink-0"
              />
              Track your favorite drivers across every race
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-300">
              <FontAwesomeIcon
                icon={faCheck}
                className="mt-0.5 text-violet-400 text-xs shrink-0"
              />
              Save custom dashboard layouts
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-300">
              <FontAwesomeIcon
                icon={faCheck}
                className="mt-0.5 text-violet-400 text-xs shrink-0"
              />
              Get push notifications for race events
            </li>
          </ul>
          <div className="flex items-center justify-between mt-3 gap-x-2">
            <button
              type="button"
              onClick={noop}
              className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-3 py-1.5 text-sm"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={noop}
              className="flex-1 rounded bg-violet-600 hover:bg-violet-500 px-3 py-1.5 text-sm font-medium text-white"
            >
              Sign Up Free
            </button>
          </div>
        </BannerShell>
      </Section>

      {/* Offline Banner */}
      <Section title="Offline Banner">
        <div className="rounded-lg overflow-hidden border border-slate-700">
          <div className="bg-yellow-600 text-black px-4 py-2 text-center font-medium">
            ⚠️ You are offline. Showing cached data from 2 minutes ago.
          </div>
        </div>
      </Section>

      {/* Update Toasts */}
      <Section title="Update Toasts">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Web update toast */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Web Update (non-staff)</p>
            <div className="w-80 bg-slate-900 rounded-md px-3 py-2 shadow border border-slate-700">
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium">New Update!</p>
              </div>
              <div className="flex items-center justify-between mt-2 w-full gap-x-2">
                <button
                  type="button"
                  onClick={noop}
                  className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-2 py-1 text-sm"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={noop}
                  className="w-full rounded animate-pulse px-2 py-1 text-sm relative"
                >
                  Updating
                  <div className="absolute top-0 left-0 h-full bg-green-600 rounded text-center px-2 fill-bar whitespace-nowrap text-slate-200 -z-10" />
                </button>
              </div>
            </div>
          </div>

          {/* Web update toast — staff */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Web Update (staff debug)</p>
            <div className="w-80 bg-slate-900 rounded-md px-3 py-2 shadow border border-slate-700">
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium">New Update!</p>
              </div>
              <div className="text-xs text-slate-400 mt-1 font-mono space-y-0.5">
                <div>bundle: 2026.03.23-abc1234</div>
                <div>config: 2026.03.23-def5678</div>
                <div>live: 2026.03.24-ghi9012</div>
                <div>deployId: deploy-abc123</div>
                <div>waitingSW: true | swUpdate: true</div>
              </div>
              <div className="flex items-center justify-between mt-2 w-full gap-x-2">
                <button
                  type="button"
                  onClick={noop}
                  className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-2 py-1 text-sm"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={noop}
                  className="w-full rounded animate-pulse px-2 py-1 text-sm relative"
                >
                  Updating
                  <div className="absolute top-0 left-0 h-full bg-green-600 rounded text-center px-2 fill-bar whitespace-nowrap text-slate-200 -z-10" />
                </button>
              </div>
            </div>
          </div>

          {/* Server update toast */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Server Update</p>
            <div className="w-80 bg-red-600 rounded-md px-3 py-2 shadow text-center animate-bounce">
              Update the Server!
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
